import React, { useEffect, useRef, useCallback } from 'react';
import { usePropertyPlmContext } from '../../properties/PropertyContextWrapper';

interface UnitPlmIndicatorsRendererProps {
  propertyId: string;
  unitType?: string;
  searchTerm?: string;
  sortColumnName?: string;
  sortDescending?: boolean;
}

export const UnitPlmIndicatorsRenderer: React.FC<
  UnitPlmIndicatorsRendererProps
> = ({ propertyId, unitType, searchTerm, sortColumnName, sortDescending }) => {
  const { hasScheduledEventResponses, unitPlmTasks } = usePropertyPlmContext();
  const renderTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const renderIndicators = useCallback(() => {
    if (!hasScheduledEventResponses) return;

    // Find all unit rows in the table
    const unitRows = document.querySelectorAll('[data-rowindex]');

    // If no rows found with data-rowindex, try alternative selectors
    if (unitRows.length === 0) {
      const alternativeRows = document.querySelectorAll('tr');

      // Process alternative rows that contain PLM cells
      alternativeRows.forEach((row) => {
        const plmCell = row.querySelector('[id^="plm-indicators-"]');
        if (plmCell) {
          // Extract unit ID from the cell ID
          const unitId = plmCell.id.replace('plm-indicators-', '');

          // Check if this unit has PLM tasks
          const unitTasks = unitPlmTasks[unitId];

          if (!unitTasks) {
            return;
          }

          // Create container for indicators
          const container = document.createElement('div');
          container.style.display = 'flex';
          container.style.gap = '4px';
          container.style.alignItems = 'center';

          // Render current task indicator (green lightning bolt)
          if (unitTasks.currentTasks && unitTasks.currentTasks.length > 0) {
            const currentIndicator = document.createElement('div');
            currentIndicator.innerHTML = '⚡';
            currentIndicator.style.color = '#4caf50';
            currentIndicator.style.cursor = 'pointer';
            currentIndicator.title =
              'Current PLM task - Click to view timeline';

            // Add click handler for current tasks
            currentIndicator.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();

              // Navigate to timeline view for the first current task
              const firstTask = unitTasks.currentTasks[0];
              if (firstTask && firstTask.plmEventResponseId) {
                window.location.href = `/plm/event-responses/${firstTask.plmEventResponseId}/timeline`;
              }
            });

            container.appendChild(currentIndicator);
          }

          // Render scheduled task indicator (blue calendar)
          if (unitTasks.scheduledTasks && unitTasks.scheduledTasks.length > 0) {
            const scheduledIndicator = document.createElement('div');
            scheduledIndicator.innerHTML = '📅';
            scheduledIndicator.style.color = '#2196f3';
            scheduledIndicator.style.cursor = 'pointer';
            scheduledIndicator.title =
              'Scheduled PLM task - Click to view calendar';

            // Add click handler for scheduled tasks
            scheduledIndicator.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();

              // Navigate to unit-specific PLM calendar view
              window.location.href = `/properties/${propertyId}/units/${unitId}/plm-schedule?propertyId=${propertyId}&unitId=${unitId}`;
            });

            container.appendChild(scheduledIndicator);
          }

          // Clear existing content and append new indicators
          plmCell.innerHTML = '';
          plmCell.appendChild(container);
        }
      });

      return; // Exit early since we processed the alternative rows
    }

    unitRows.forEach((row) => {
      const rowIndex = row.getAttribute('data-rowindex');
      if (rowIndex === null) return;

      // Find the PLM indicators cell for this row
      const plmCell = row.querySelector('[id^="plm-indicators-"]');
      if (!plmCell) {
        return;
      }

      // Extract unit ID from the cell ID
      const unitId = plmCell.id.replace('plm-indicators-', '');

      // Check if this unit has PLM tasks
      const unitTasks = unitPlmTasks[unitId];

      if (!unitTasks) {
        return;
      }

      // Create container for indicators
      const container = document.createElement('div');
      container.style.display = 'flex';
      container.style.gap = '4px';
      container.style.alignItems = 'center';

      // Render current task indicator (green lightning bolt)
      if (unitTasks.currentTasks && unitTasks.currentTasks.length > 0) {
        const currentIndicator = document.createElement('div');
        currentIndicator.innerHTML = '⚡';
        currentIndicator.style.color = '#4caf50';
        currentIndicator.style.cursor = 'pointer';
        currentIndicator.title = 'Current PLM task - Click to view timeline';

        // Add click handler for current tasks
        currentIndicator.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Navigate to timeline view for the first current task
          const firstTask = unitTasks.currentTasks[0];
          if (firstTask && firstTask.plmEventResponseId) {
            window.location.href = `/plm/event-responses/${firstTask.plmEventResponseId}/timeline`;
          }
        });

        container.appendChild(currentIndicator);
      }

      // Render scheduled task indicator (blue calendar)
      if (unitTasks.scheduledTasks && unitTasks.scheduledTasks.length > 0) {
        const scheduledIndicator = document.createElement('div');
        scheduledIndicator.innerHTML = '📅';
        scheduledIndicator.style.color = '#2196f3';
        scheduledIndicator.style.cursor = 'pointer';
        scheduledIndicator.title =
          'Scheduled PLM task - Click to view calendar';

        // Add click handler for scheduled tasks
        scheduledIndicator.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Navigate to unit-specific calendar view
          window.location.href = `/properties/${propertyId}/units/${unitId}/schedule`;
        });

        container.appendChild(scheduledIndicator);
      }

      // Clear existing content and append new indicators
      plmCell.innerHTML = '';
      plmCell.appendChild(container);
    });
  }, [hasScheduledEventResponses, unitPlmTasks, propertyId]);

  useEffect(() => {
    // Wait for the table to be rendered before trying to find elements
    renderTimeoutRef.current = setTimeout(() => {
      renderIndicators();
    }, 100);

    return () => {
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
      }
    };
  }, [
    hasScheduledEventResponses,
    unitPlmTasks,
    renderIndicators,
    unitType,
    searchTerm,
    sortColumnName,
    sortDescending,
    propertyId,
  ]);

  // Don't render anything visible - this component just manages the indicators
  return null;
};
